import IPhoneMessageTail from '@/assets/iphone-message-tail.svg?react'
import { Faq, FaqItem, FaqPanel, FaqTrigger } from '@/components/faq'
import { IPhoneIcon } from '@/components/iphone-icon'
import {
  SupportDialog,
  SupportDialogPopup,
  SupportDialogTrigger,
} from '@/components/support-dialog'
import {
  Tool,
  ToolContent,
  ToolDescription,
  ToolHeading,
  ToolList,
  ToolSection,
  ToolSeparator,
  ToolSubheading,
} from '@/components/tool'
import { logEvent } from '@/util/analytics'
import { toPng } from '@/util/image'
import { Button } from '@beeirl/ui/button'
import { DateTimePicker } from '@beeirl/ui/date-time-picker'
import { FileUpload } from '@beeirl/ui/file-upload'
import { Form } from '@beeirl/ui/form'
import { LineIcon } from '@beeirl/ui/line-icon'
import { Menu } from '@beeirl/ui/menu'
import { Select } from '@beeirl/ui/select'
import { SolidIcon } from '@beeirl/ui/solid-icon'
import { cn } from '@beeirl/ui/utils/cn'
import { fileToDataURL } from '@beeirl/ui/utils/file'
import {
  differenceInMinutes,
  format as formatDate,
  isSameDay,
  isToday,
  isWithinInterval,
  isYesterday,
  subDays,
} from 'date-fns'
import React from 'react'

const DEFAULT_INPUT_PLACEHOLDER = 'Postfully.app'
const DEFAULT_MESSAGES: Message[] = [
  {
    actor: 'sender',
    date: new Date(),
    image: '',
    method: 'data',
    status: 'default',
    text: 'Did you see that viral video of the cat playing piano? 😂',
  },
  {
    actor: 'recipient',
    date: new Date(),
    image: '',
    method: 'unknown',
    status: 'default',
    text: "OMG yes! I can't believe it got 1 million views in one day! 🎹🐱",
  },
]
const DEFAULT_RECIPIENT_NAME = 'Postfully'

const TIME_FORMATS = {
  '12h': 'Standard (12-hour clock)',
  '24h': 'Military (24-hour clock)',
}

const MODES = {
  dark: 'Dark',
  light: 'Light',
}

type Message = {
  actor: 'sender' | 'recipient'
  date: Date
  image: string
  method: 'data' | 'text' | 'unknown'
  status: 'default' | 'delivered' | 'not_delivered' | 'read'
  text: string
}

function MessageTypeIndicator({
  className,
  color,
  style,
  ...props
}: React.ComponentProps<'div'> & {
  color: string
}) {
  return (
    <div
      className={cn(
        'focus-visible:ring-accent-600 size-[12px] rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        className
      )}
      style={{ backgroundColor: color, ...style }}
      {...props}
    />
  )
}

function MessageImageBubble({ hideTail, message }: { hideTail: boolean; message: Message }) {
  const [color, setColor] = React.useState<string>()
  return (
    <div className="flex items-center gap-[6px]">
      <div className="relative max-w-[198px]">
        <img
          className="z-1 relative rounded-[14px] object-cover"
          src={message.image}
          onLoad={(e) => {
            const element = e.target as HTMLImageElement
            const width = element.naturalWidth
            const height = element.naturalHeight
            if (!width || !height) return

            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            if (!ctx) return
            ctx.drawImage(element, 0, 0, width, height)

            // bottom-left for recipient, bottom-right for sender
            const x = message.actor === 'recipient' ? 0 : width - 1
            const pixel = ctx.getImageData(x, height - 1, 1, 1).data
            const rgba = `rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3] / 255})`
            setColor(rgba)
          }}
        />
        {!hideTail && (
          <IPhoneMessageTail
            className={cn(
              'absolute bottom-[-1px] h-[18px]',
              message.actor === 'recipient' && 'left-[-4.2578px] scale-x-[-1]',
              message.actor === 'sender' && 'right-[-4.2820px]'
            )}
            style={{ color }}
          />
        )}
      </div>
      {message.actor === 'sender' && message.status === 'not_delivered' && (
        <IPhoneIcon.ExclamationmarkCircle className="size-[18px] text-[#FD3B31]" />
      )}
    </div>
  )
}

function MessageTextBubble({
  hideTail,
  message,
  mode,
}: {
  hideTail: boolean
  message: Message
  mode: 'dark' | 'light'
}) {
  return (
    <div className="flex items-center gap-[6px]">
      <div
        className={cn(
          'relative flex max-w-[198px] break-words rounded-[14px] px-[10px] py-[6px]',
          message.actor === 'recipient' &&
            cn(mode === 'dark' && 'bg-[#262628]', mode === 'light' && 'bg-[#E9E9EB]'),
          message.actor === 'sender' &&
            cn(
              message.method === 'data' && 'bg-[#007AFF]',
              message.method === 'text' && 'bg-[#34C759]'
            )
        )}
      >
        <span
          className={cn(
            'w-full whitespace-pre-wrap text-[13px]/4',
            message.actor === 'recipient' &&
              cn(mode === 'dark' && 'text-white', mode === 'light' && 'text-black'),
            message.actor === 'sender' && 'text-white'
          )}
        >
          {message.text}
        </span>
        {!hideTail && (
          <IPhoneMessageTail
            className={cn(
              'absolute bottom-[-1px] h-[18px]',
              message.actor === 'recipient' &&
                cn(
                  'left-[-4.2578px] scale-x-[-1]',
                  mode === 'dark' && 'text-[#262628]',
                  mode === 'light' && 'text-[#E9E9EB]'
                ),
              message.actor === 'sender' &&
                cn(
                  'right-[-4.2820px]',
                  message.method === 'data' && 'text-[#007AFF]',
                  message.method === 'text' && 'text-[#34C759]'
                )
            )}
          />
        )}
      </div>
      {message.actor === 'sender' && message.status === 'not_delivered' && (
        <IPhoneIcon.ExclamationmarkCircle className="size-[18px] text-[#FD3B31]" />
      )}
    </div>
  )
}

function MessageDevice({
  className,
  device,
  inputPlaceholder,
  messages,
  mode = 'light',
  recipient,
  scrollTop,
  style,
  timeFormat,
  onScroll,
  ...props
}: React.ComponentProps<'div'> & {
  device: {
    time: string
  }
  inputPlaceholder?: string
  messages: Message[]
  mode: 'dark' | 'light'
  recipient: {
    avatar?: string
    name?: string
  }
  scrollTop?: number
  timeFormat: '12h' | '24h'
}) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (scrollContainerRef.current && scrollTop) {
      scrollContainerRef.current.scrollTop = scrollTop
    }
  }, [scrollTop])

  const recipientName = recipient.name || DEFAULT_RECIPIENT_NAME
  const recipientNameParts = recipientName.split(' ').filter(Boolean)
  const recipientInitials =
    `${recipientNameParts[0][0] || ''}${recipientNameParts[1]?.[0] || ''}`.toUpperCase()
  const recipientDisplayName =
    recipientNameParts.length > 1
      ? `${recipientNameParts[0][0]} ${recipientNameParts[1]}`
      : recipientNameParts[0]

  const isGroupedMessage = React.useCallback(
    (messageIndex: number) => {
      const currentMessage = messages[messageIndex]
      const nextMessage = messages[messageIndex + 1]

      if (nextMessage && nextMessage.actor === currentMessage.actor) {
        const isWithinFiveMinutes = differenceInMinutes(nextMessage.date, currentMessage.date) <= 5
        if (isWithinFiveMinutes) {
          return true
        }
      }

      return false
    },
    [messages]
  )

  return (
    <div
      className={cn(
        'relative flex h-[684px] w-[316px] flex-col bg-white font-[Inter,_sans-serif]',
        className
      )}
      style={{ fontFeatureSettings: 'normal', ...style }}
      {...props}
    >
      <div
        className={cn(
          'absolute inset-x-0 top-0 z-10 pb-[5px] pt-[13px]',
          mode === 'dark' && 'bg-[#121212]',
          mode === 'light' && 'bg-[#F6F5F6]'
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between pl-[31px] pr-[25px]',
            mode === 'dark' && 'text-white',
            mode === 'light' && 'text-black'
          )}
        >
          <span className="text-[14px] font-semibold">
            {device.time
              ? formatDate(
                  new Date(`1970-01-01T${device.time}`),
                  {
                    '12h': 'h:mm',
                    '24h': 'HH:mm',
                  }[timeFormat]
                )
              : { '12h': '9:41', '24h': '09:41' }[timeFormat]}
          </span>
          <div className="flex items-center gap-[5px]">
            <IPhoneIcon.Cellularbars className="h-[10px]" />
            <IPhoneIcon.Wifi className="h-[11px]" />
            <IPhoneIcon.Battery75 className="h-[11px]" />
          </div>
        </div>
        <div className="mt-[16px]">
          <div className="relative flex items-center justify-center">
            <IPhoneIcon.ChevronLeft className="absolute left-[6px] size-[25px] text-[#007AFF]" />
            <div className="size-[40px] overflow-hidden rounded-full">
              {!recipient.avatar && (
                <div className="flex size-full items-center justify-center bg-gradient-to-b from-[#A5ABB9] to-[#858994] text-center">
                  <span className="text-[18px] font-semibold text-white">{recipientInitials}</span>
                </div>
              )}
              {recipient.avatar && (
                <img src={recipient.avatar} className="size-full object-cover" />
              )}
            </div>
            <IPhoneIcon.Video className="absolute right-[17px] size-[25px] text-[#007AFF]" />
          </div>
          <div className="mx-auto mt-[2.5px] flex max-w-[65%] items-center justify-center overflow-hidden">
            <span
              className={cn(
                'truncate text-[9px]',
                mode === 'dark' && 'text-white',
                mode === 'light' && 'text-black'
              )}
            >
              {recipientDisplayName}
            </span>
            <IPhoneIcon.ChevronRight
              className={cn(
                'size-[9px]',
                mode === 'dark' && 'text-[#535356]',
                mode === 'light' && 'text-[#C1C1C3]'
              )}
            />
          </div>
        </div>
        <svg className="absolute inset-x-0 bottom-0" height="0.5" width="100%">
          <line
            className={cn(
              mode === 'dark' && 'stroke-[#262626]',
              mode === 'light' && 'stroke-[#B2B2B2]'
            )}
            x1="0"
            y1="0"
            x2="100%"
            y2="0"
            strokeWidth="1"
          />
        </svg>
      </div>
      <div
        className={cn(
          'no-scrollbar relative flex flex-1 flex-col overflow-auto px-[11px] pb-[81px] pt-[110px]',
          mode === 'dark' && 'bg-black',
          mode === 'light' && 'bg-white'
        )}
        ref={scrollContainerRef}
        onScroll={onScroll}
      >
        {messages.map((message, index) => {
          const defaultMessage = DEFAULT_MESSAGES[index]
          const isGrouped = isGroupedMessage(index)
          return (
            <React.Fragment key={`message-${index}`}>
              {(index === 0 || !isSameDay(message.date, messages[index - 1].date)) && (
                <div className="flex justify-center pb-[6px] pt-[11px] text-center">
                  <span
                    className={cn(
                      'text-[9px] font-medium',
                      mode === 'dark' && 'text-[#8D8D93]',
                      mode === 'light' && 'text-[#8A898E]'
                    )}
                  >
                    {(() => {
                      if (isToday(message.date)) {
                        return `Today ${formatDate(
                          message.date,
                          {
                            '12h': 'h:mm a',
                            '24h': 'HH:mm',
                          }[timeFormat]
                        )}`
                      }
                      if (isYesterday(message.date)) {
                        return `Yesterday ${formatDate(
                          message.date,
                          {
                            '12h': 'h:mm a',
                            '24h': 'HH:mm',
                          }[timeFormat]
                        )}`
                      }
                      if (
                        isWithinInterval(message.date, {
                          start: subDays(new Date(), 6),
                          end: new Date(),
                        })
                      ) {
                        return formatDate(
                          message.date,
                          {
                            '12h': 'EEEE h:mm a',
                            '24h': 'EEEE HH:mm',
                          }[timeFormat]
                        )
                      }
                      return formatDate(
                        message.date,
                        {
                          '12h': "EEE, MMM d 'at' h:mm a",
                          '24h': "EEE, MMM d 'at' HH:mm",
                        }[timeFormat]
                      )
                    })()}
                  </span>
                </div>
              )}
              <div
                className={cn(
                  'flex flex-col gap-[2px]',
                  isGrouped && 'pb-[2px]',
                  !isGrouped && 'pb-[6px]'
                )}
              >
                <div
                  className={cn(
                    'flex flex-col gap-[2px]',
                    message.actor === 'recipient' && 'items-start',
                    message.actor === 'sender' && 'items-end'
                  )}
                >
                  {message.image && (
                    <MessageImageBubble hideTail={isGrouped || !!message.text} message={message} />
                  )}
                  {(message.text || (defaultMessage && !message.image)) && (
                    <MessageTextBubble
                      message={{
                        ...message,
                        text: message.text || defaultMessage?.text || '',
                      }}
                      mode={mode}
                      hideTail={isGrouped}
                    />
                  )}
                </div>
                {message.actor === 'sender' && message.status === 'delivered' && (
                  <div className="flex justify-end text-[9px] font-semibold">
                    <span
                      className={cn(
                        mode === 'dark' && 'text-[#8D8D93]',
                        mode === 'light' && 'text-[#8A898E]'
                      )}
                    >
                      Delivered
                    </span>
                  </div>
                )}
                {message.actor === 'sender' && message.status === 'not_delivered' && (
                  <div className="flex justify-end text-[9px] font-semibold">
                    <span className="text-[#FD3B31]">Not Delivered</span>
                  </div>
                )}
                {message.actor === 'sender' && message.status === 'read' && (
                  <div className="flex justify-end text-[9px] font-semibold">
                    <span
                      className={cn(
                        mode === 'dark' && 'text-[#8D8D93]',
                        mode === 'light' && 'text-[#8A898E]'
                      )}
                    >
                      Read
                    </span>
                  </div>
                )}
              </div>
            </React.Fragment>
          )
        })}
      </div>
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 z-10 flex flex-col',
          mode === 'dark' && 'bg-black',
          mode === 'light' && 'bg-[#FCFCFC]'
        )}
      >
        <div className="mb-[23px] flex items-center gap-[10px] px-[11px] pt-[4px]">
          <div
            className={cn(
              'flex size-[28px] items-center justify-center rounded-full',
              mode === 'dark' && 'bg-[#1F1F21] text-[#A3A3A5]',
              mode === 'light' && 'bg-[#E8E7EC] text-[#7E7F84]'
            )}
          >
            <IPhoneIcon.Plus className="size-[14px]" />
          </div>
          <div
            className={cn(
              'flex flex-1 items-center overflow-hidden rounded-full border px-[10px] py-[4.5px]',
              mode === 'dark' && 'border-[#212223]',
              mode === 'light' && 'border-[#DEDEDE]'
            )}
          >
            <span
              className={cn(
                'flex-1 truncate text-[13px]',
                mode === 'dark' && 'text-[#464649]',
                mode === 'light' && 'text-[#C1C2C4]'
              )}
            >
              {inputPlaceholder || DEFAULT_INPUT_PLACEHOLDER}
            </span>
            <IPhoneIcon.Mic
              className={cn(
                'size-[16px]',
                mode === 'dark' && 'text-[#4A4B4D]',
                mode === 'light' && 'text-[#ACB4B7]'
              )}
            />
          </div>
        </div>
        <div className="flex items-center justify-center pb-[6px]">
          <div
            className={cn(
              'h-[4px] w-[112px] rounded-full',
              mode === 'dark' && 'bg-white',
              mode === 'light' && 'bg-black'
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default function TextMessageGenerator() {
  const avatarInputRef = React.useRef<HTMLInputElement>(null)
  const imageInputsRef = React.useRef<HTMLInputElement[]>([])
  const scrollTopRef = React.useRef(0)

  const [loading, setLoading] = React.useState(false)

  const form = Form.use({
    defaultValues: {
      device: {
        time: '',
      },
      inputPlaceholder: '',
      messages: [
        { actor: 'sender', date: new Date(), method: 'data', status: 'default', text: '' },
        { actor: 'recipient', date: new Date(), method: 'unknown', status: 'default', text: '' },
      ] as Message[],
      mode: 'light' as keyof typeof MODES,
      recipient: {
        avatar: '',
        name: '',
      },
      timeFormat: '12h' as keyof typeof TIME_FORMATS,
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      const url = await toPng(<MessageDevice {...value} scrollTop={scrollTopRef.current} />, {
        delay: value.messages.some((message) => !!message.image) ? 500 : 0,
        restoreScrollPosition: true,
      })
      const link = document.createElement('a')
      link.download = `postfully-text-message-${Date.now()}.png`
      link.href = url
      link.click()
      logEvent('text_message_downloaded', {
        deviceTime: value.device.time,
        timeFormat: value.timeFormat,
        recipientAvatar: !!value.recipient.avatar,
        recipientName: value.recipient.name,
        inputPlaceholder: value.inputPlaceholder,
        messages: value.messages.map((message) => ({
          actor: message.actor,
          date: message.date.toISOString(),
          hasImage: !!message.image,
          status: message.status,
          text: message.text,
        })),
        mode: value.mode,
      })
      setLoading(false)
    },
  })

  return (
    <Tool>
      <ToolSection className="max-w-4xl">
        <ToolHeading>Fake Text Generator</ToolHeading>
        <ToolDescription>
          Create custom text messages for your text story videos. Free to use, no email required.
        </ToolDescription>
        <ToolContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
          >
            <div className="shadow-xs flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 md:flex-row">
              <div className="flex-1 space-y-4 overflow-auto p-3 max-sm:pb-6 md:pr-5">
                <form.AppField name="recipient.avatar">
                  {(field) => (
                    <field.Root className="items-start">
                      <field.Label>Recipient Avatar</field.Label>
                      <field.Control
                        render={
                          <div className="flex items-center gap-4">
                            <FileUpload.Root
                              defaultValue={field.options.defaultValue}
                              name={field.name}
                              value={field.state.value}
                              onValueChange={async (value) =>
                                field.setValue(await fileToDataURL(value))
                              }
                            >
                              <FileUpload.ImageInput ref={avatarInputRef} />
                            </FileUpload.Root>
                            <Button
                              color="gray"
                              size="xs"
                              variant="outline"
                              onClick={() => {
                                avatarInputRef.current?.click()
                              }}
                            >
                              Upload
                            </Button>
                          </div>
                        }
                      />
                    </field.Root>
                  )}
                </form.AppField>
                <form.AppField name="recipient.name">
                  {(field) => (
                    <field.Root>
                      <field.Label>Recipient Name</field.Label>
                      <field.Control
                        render={
                          <field.TextInput
                            autoFocus
                            placeholder={`e.g. ${DEFAULT_RECIPIENT_NAME}`}
                          />
                        }
                      />
                    </field.Root>
                  )}
                </form.AppField>
                <form.AppField name="inputPlaceholder">
                  {(field) => (
                    <field.Root>
                      <field.Label>Message Input</field.Label>
                      <field.Control
                        render={
                          <field.TextInput placeholder={`e.g. ${DEFAULT_INPUT_PLACEHOLDER}`} />
                        }
                      />
                    </field.Root>
                  )}
                </form.AppField>
                <form.AppField name="device.time">
                  {(field) => (
                    <field.Root>
                      <field.Label>Device Time</field.Label>
                      <field.Control
                        render={
                          <form.Subscribe selector={(state) => state.values.timeFormat}>
                            {(format) => <field.TimeInput format={format} />}
                          </form.Subscribe>
                        }
                      />
                    </field.Root>
                  )}
                </form.AppField>
                <form.AppField name="timeFormat">
                  {(field) => (
                    <field.Root>
                      <field.Label>Time format</field.Label>
                      <field.Control
                        render={
                          <field.Select>
                            {(props) => (
                              <Select.Root {...props}>
                                <Select.Trigger
                                  render={
                                    <Select.Control placeholder={TIME_FORMATS[field.state.value]} />
                                  }
                                />
                                <Select.Positioner>
                                  <Select.Popup>
                                    {Object.entries(TIME_FORMATS).map(([value, label]) => (
                                      <Select.Item key={value} value={value}>
                                        {label}
                                      </Select.Item>
                                    ))}
                                  </Select.Popup>
                                </Select.Positioner>
                              </Select.Root>
                            )}
                          </field.Select>
                        }
                      />
                    </field.Root>
                  )}
                </form.AppField>
                <form.AppField name="mode">
                  {(field) => (
                    <field.Root>
                      <field.Label>Mode</field.Label>
                      <field.Control
                        render={
                          <field.Select>
                            {(props) => (
                              <Select.Root {...props}>
                                <Select.Trigger
                                  render={<Select.Control placeholder={MODES[field.state.value]} />}
                                />
                                <Select.Positioner>
                                  <Select.Popup>
                                    {Object.entries(MODES).map(([value, label]) => (
                                      <Select.Item key={value} value={value}>
                                        {label}
                                      </Select.Item>
                                    ))}
                                  </Select.Popup>
                                </Select.Positioner>
                              </Select.Root>
                            )}
                          </field.Select>
                        }
                      />
                    </field.Root>
                  )}
                </form.AppField>
                <form.AppField mode="array" name="messages">
                  {(field) => (
                    <field.Root>
                      <div className="flex w-full flex-col gap-1">
                        <field.Label>Messages</field.Label>
                        <div className="flex w-full flex-col items-start gap-3">
                          <div className="flex w-full flex-col gap-2">
                            {field.state.value.map((message, index) => (
                              <div
                                className="shadow-xs relative flex rounded-md px-1 ring ring-gray-200"
                                key={`message-${index}`}
                              >
                                <form.Field name={`messages[${index}]`}>
                                  {(subField) => (
                                    <Select.Root
                                      value={JSON.stringify({
                                        actor: subField.state.value.actor,
                                        method: subField.state.value.method,
                                      })}
                                      onValueChange={(value: string) => {
                                        subField.setValue({
                                          ...subField.state.value,
                                          ...JSON.parse(value),
                                        })
                                      }}
                                    >
                                      <Select.Trigger
                                        render={
                                          <button
                                            className="focus-visible:ring-accent-600 flex h-9 w-7 shrink-0 cursor-pointer items-center justify-center focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                                            type="button"
                                          >
                                            <MessageTypeIndicator
                                              color={
                                                subField.state.value.actor === 'sender'
                                                  ? subField.state.value.method === 'data'
                                                    ? '#007AFF'
                                                    : '#34C759'
                                                  : '#D1D1D3'
                                              }
                                            />
                                          </button>
                                        }
                                      />
                                      <Select.Positioner align="start" alignItemWithTrigger={false}>
                                        <Select.Popup>
                                          <Select.Item
                                            value={JSON.stringify({
                                              actor: 'recipient',
                                              method: 'unknown',
                                            })}
                                          >
                                            <MessageTypeIndicator color="#D1D1D3" />
                                            Recipient
                                          </Select.Item>
                                          <Select.Item
                                            value={JSON.stringify({
                                              actor: 'sender',
                                              method: 'data',
                                            })}
                                          >
                                            <MessageTypeIndicator color="#007AFF" />
                                            Sender
                                          </Select.Item>
                                          <Select.Item
                                            value={JSON.stringify({
                                              actor: 'sender',
                                              method: 'text',
                                            })}
                                          >
                                            <MessageTypeIndicator color="#34C759" />
                                            Sender
                                          </Select.Item>
                                        </Select.Popup>
                                      </Select.Positioner>
                                    </Select.Root>
                                  )}
                                </form.Field>
                                <form.Field name={`messages[${index}].image`}>
                                  {(field) => (
                                    <button
                                      className="focus-visible:ring-accent-600 group flex h-9 w-7 shrink-0 cursor-pointer items-center justify-center focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                                      type="button"
                                      onClick={() => {
                                        imageInputsRef.current[index].click()
                                      }}
                                    >
                                      {field.state.value ? (
                                        <img
                                          src={field.state.value}
                                          className="size-4.5 shrink-0 rounded-sm object-cover"
                                        />
                                      ) : (
                                        <LineIcon.Image className="size-3.5 text-gray-400 group-hover:text-gray-600" />
                                      )}
                                      <input
                                        accept="image/*"
                                        className="hidden"
                                        ref={(element) => {
                                          if (element) {
                                            imageInputsRef.current[index] = element
                                          }
                                        }}
                                        type="file"
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0]
                                          if (!file) return
                                          field.setValue(await fileToDataURL(file))
                                        }}
                                      />
                                    </button>
                                  )}
                                </form.Field>
                                <form.Field name={`messages[${index}].text`}>
                                  {(subField) => (
                                    <textarea
                                      className="field-sizing-content focus-visible:ring-accent-600 min-h-9 w-full resize-none p-2 text-sm placeholder:text-gray-400 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                                      placeholder="Type a message..."
                                      value={subField.state.value}
                                      onChange={(e) => subField.setValue(e.target.value)}
                                    />
                                  )}
                                </form.Field>
                                <form.Field name={`messages[${index}].date`}>
                                  {(subField) => (
                                    <Menu.Root>
                                      <Menu.Trigger
                                        render={
                                          <button
                                            className="focus-visible:ring-accent-600 group flex h-9 w-7 shrink-0 cursor-pointer items-center justify-center focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                                            type="button"
                                          >
                                            <SolidIcon.DotsHorizontal className="size-[14px] text-gray-400 group-hover:text-gray-600" />
                                          </button>
                                        }
                                      />
                                      <Menu.Positioner align="end">
                                        <Menu.Popup>
                                          <Menu.Root>
                                            <Menu.SubmenuTrigger>Date</Menu.SubmenuTrigger>
                                            <Menu.Positioner>
                                              <Menu.Popup className="p-2">
                                                <DateTimePicker.Root
                                                  value={subField.state.value}
                                                  onValueChange={(value) =>
                                                    subField.setValue(value)
                                                  }
                                                >
                                                  <DateTimePicker.Input />
                                                  <DateTimePicker.Calendar toDate={new Date()} />
                                                </DateTimePicker.Root>
                                              </Menu.Popup>
                                            </Menu.Positioner>
                                          </Menu.Root>
                                          <form.Field name={`messages[${index}].status`}>
                                            {(subField) => {
                                              if (field.state.value[index].actor === 'recipient')
                                                return null
                                              return (
                                                <Menu.Root>
                                                  <Menu.SubmenuTrigger>Status</Menu.SubmenuTrigger>
                                                  <Menu.Positioner>
                                                    <Menu.Popup>
                                                      <Menu.RadioGroup
                                                        value={subField.state.value}
                                                        onValueChange={(value) =>
                                                          subField.setValue(value)
                                                        }
                                                      >
                                                        <Menu.RadioItem value="default">
                                                          Default
                                                        </Menu.RadioItem>
                                                        <Menu.RadioItem value="delivered">
                                                          Delivered
                                                        </Menu.RadioItem>
                                                        <Menu.RadioItem value="not_delivered">
                                                          Not Delivered
                                                        </Menu.RadioItem>
                                                        <Menu.RadioItem value="read">
                                                          Read
                                                        </Menu.RadioItem>
                                                      </Menu.RadioGroup>
                                                    </Menu.Popup>
                                                  </Menu.Positioner>
                                                </Menu.Root>
                                              )
                                            }}
                                          </form.Field>
                                          <Menu.Item
                                            className="text-red-500"
                                            onClick={() => {
                                              imageInputsRef.current.splice(index, 1)
                                              console.log(imageInputsRef.current)
                                              field.removeValue(index)
                                            }}
                                          >
                                            Delete
                                          </Menu.Item>
                                        </Menu.Popup>
                                      </Menu.Positioner>
                                    </Menu.Root>
                                  )}
                                </form.Field>
                              </div>
                            ))}
                          </div>
                          <Button
                            className="rounded-full"
                            color="gray"
                            highContrast
                            size="sm"
                            variant="soft"
                            onClick={() => {
                              const prevMessage = field.state.value[field.state.value.length - 1]
                              field.pushValue({
                                actor: prevMessage?.actor || 'sender',
                                date: prevMessage?.date || new Date(),
                                image: '',
                                method: prevMessage?.method || 'data',
                                status: 'default',
                                text: '',
                              })
                            }}
                          >
                            <LineIcon.Plus />
                            Add Message
                          </Button>
                        </div>
                      </div>
                    </field.Root>
                  )}
                </form.AppField>
              </div>
              <div className="flex flex-1 flex-col items-center justify-center gap-6 rounded-xl bg-gray-50 ring ring-gray-100">
                <div className="flex w-full justify-end space-x-1.5 p-3">
                  <Button
                    className="rounded-full"
                    color="gray"
                    size="sm"
                    variant="surface"
                    onClick={() => form.reset()}
                  >
                    Reset
                  </Button>
                  <form.AppForm>
                    <form.SubmitButton className="rounded-full" disabled={loading} size="sm">
                      {loading ? 'Downloading...' : 'Download'}
                    </form.SubmitButton>
                  </form.AppForm>
                </div>
                <div className="flex flex-1 flex-col justify-center gap-3">
                  <form.Subscribe selector={(state) => state.values}>
                    {(values) => (
                      <MessageDevice
                        {...values}
                        className="max-sm:scale-80 overflow-hidden rounded-2xl border border-gray-100 shadow-lg"
                        onScroll={(e) => {
                          scrollTopRef.current = e.currentTarget.scrollTop
                        }}
                      />
                    )}
                  </form.Subscribe>
                  <div className="flex items-center justify-center gap-1">
                    <LineIcon.Info className="size-3 text-gray-400" />
                    <span className="text-xs text-gray-400">Preview is scrollable</span>
                  </div>
                </div>
                <div className="flex justify-center p-3">
                  <SupportDialog>
                    <SupportDialogTrigger
                      render={
                        <Button className="rounded-full" color="gray" size="sm" variant="surface">
                          <LineIcon.Bulb />
                          Suggest a feature
                        </Button>
                      }
                    />
                    <SupportDialogPopup
                      defaultCategory="feedback"
                      location="text_message_generator"
                    />
                  </SupportDialog>
                </div>
              </div>
            </div>
          </form>
        </ToolContent>
      </ToolSection>
      <ToolSeparator />
      <ToolSection>
        <ToolSubheading>Frequently Asked Questions</ToolSubheading>
        <ToolContent className="mx-auto max-w-2xl">
          <Faq>
            <FaqItem>
              <FaqTrigger>How to generate fake texts?</FaqTrigger>
              <FaqPanel>
                Creating fake text messages is easy with our Fake Text Generator. Start by
                customizing recipient avatar, recipient name, and messages. Once you are happy with
                the result, you can download the fake text messages as an image and use it in your
                videos.
              </FaqPanel>
            </FaqItem>
            <FaqItem>
              <FaqTrigger>Why aren't all my messages visible in the downloaded image?</FaqTrigger>
              <FaqPanel>
                If you have multiple messages that extend beyond the visible area, you can scroll to
                position the messages exactly where you want them before downloading. The screenshot
                will capture the messages exactly as they appear in the preview, so simply scroll to
                your desired position and click the download button.
              </FaqPanel>
            </FaqItem>
            <FaqItem>
              <FaqTrigger>Is it okay to use fake texts?</FaqTrigger>
              <FaqPanel>
                Using fake text texts for parody, education, or mockups is generally acceptable.
                However, you should always make it clear that the fake texts are not real to avoid
                misinformation.
              </FaqPanel>
            </FaqItem>
            <FaqItem>
              <FaqTrigger>Is the Fake Text Generator free to use?</FaqTrigger>
              <FaqPanel>
                Yes, the Fake Text Generator is completely free to use. We believe in offering
                valuable tools to enhance your creative process at no cost. Explore our full suite
                of{' '}
                <a className="text-accent-600" href="/">
                  free content creation tools
                </a>{' '}
                designed to support you.
              </FaqPanel>
            </FaqItem>
          </Faq>
        </ToolContent>
      </ToolSection>
      <ToolSeparator />
      <ToolSection>
        <ToolSubheading>Discover more Tools</ToolSubheading>
        <ToolContent>
          <ToolList
            tools={[
              'tiktok-comment-generator',
              'instagram-comment-generator',
              'reddit-post-template',
              'text-box-generator',
            ]}
          />
        </ToolContent>
      </ToolSection>
    </Tool>
  )
}
